/**
 * Theme registry for Caliu.
 *
 * Each theme defines a color palette (light + dark). Fonts are NOT part of a
 * theme — they live in `packages/styles/fonts/catalog.json` and are configured
 * via a separate `FontPreferences` setting per client.
 *
 * Theme JSON files in this directory are the source of truth.
 * The `generate-tokens.mjs` script reads them to produce `tokens.css`.
 */

import caliu from "./caliu.json";
import tinta from "./tinta.json";
import magrana from "./magrana.json";
import glacera from "./glacera.json";
import turquesa from "./turquesa.json";
import cel from "./cel.json";
import espigol from "./espigol.json";
import coure from "./coure.json";
import ginesta from "./ginesta.json";
import pissarra from "./pissarra.json";
import aplec from "./aplec.json";
import vitrall from "./vitrall.json";
import flamenc from "./flamenc.json";
import safra from "./safra.json";
import selva from "./selva.json";
import ultramar from "./ultramar.json";
import neo from "./neo.json";
import llampec from "./llampec.json";
import paper from "./paper.json";
import llapis from "./llapis.json";
import fluor from "./fluor.json";
import comic from "./comic.json";

export interface ThemeDefinition {
  id: string;
  lightName: string;
  darkName: string;
  description: string;
  light: Record<string, string>;
  dark: Record<string, string>;
}

/**
 * All available themes, in display order: a curated shuffle that opens with
 * the default (Caliu) and deliberately AVOIDS any chromatic progression —
 * neighbors (and each 2-column picker row) contrast in hue and temperature,
 * and same-family themes are spread apart, so the grid reads eclectic rather
 * than sorted. Order here is presentation only; never rely on `themes[0]`
 * being the default (that is `DEFAULT_THEME_ID`).
 */
export const themes: ThemeDefinition[] = [
  caliu,
  neo,
  safra,
  paper,
  glacera,
  tinta,
  magrana,
  turquesa,
  llampec,
  coure,
  flamenc,
  cel,
  comic,
  fluor,
  selva,
  vitrall,
  ultramar,
  llapis,
  espigol,
  ginesta,
  aplec,
  pissarra,
];

/** Theme lookup by ID. */
export const themesById: Record<string, ThemeDefinition> = Object.fromEntries(
  themes.map((t) => [t.id, t]),
);

/** All valid theme IDs. */
export const themeIds = themes.map((t) => t.id);

/** The default theme ID. */
export const DEFAULT_THEME_ID = "caliu";
